import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { throwError } from 'rxjs';

/** Log de erro para identificar falhas em GET Plans / checkout preview (ver docs/PRECOS_PLANOS_API.md). */
function logPlansError(context: string, err: { status?: number; statusText?: string; url?: string; error?: unknown; message?: string }): void {
  console.error(`[Pipeup] ${context} falhou`, {
    status: err?.status,
    statusText: err?.statusText,
    url: err?.url,
    body: err?.error,
    message: err?.message,
  });
}
import { ApiService } from './api.service';
import { PlanApi, PlanCode } from '../models/plan.model';

// Models
export interface PlanListResponse {
  data: PlanApi[];
}

/** Alteração agendada para o próximo ciclo (GET /api/subscriptions/current). */
export interface ScheduledChange {
  id: number;
  changeType: 'PlanDowngrade' | 'SeatReduction' | 'AddOnRemoval' | 'CycleToMonthly';
  applyAt: string;
  newPlanCode?: string;
  targetExtraSeats?: number;
  addOnCode?: string;
  mensagem?: string;
}

export interface Subscription {
  id: number;
  status: 'Trialing' | 'Active' | 'PastDue' | 'Canceled' | 'Expired';
  planCode: string;
  trialEndsAt?: string | null;
  trialDaysRemaining?: number | null;
  seatsIncluded: number;
  seatsUsed: number;
  /** Total de assentos disponíveis (incluídos + extras atribuídos + pendentes). Limite = este valor. Spec back: seatsTotal. */
  seatsTotal?: number;
  /** Assentos extras já contratados (pagos). Em trial = 0. Limite = seatsIncluded + extraSeatsContracted. */
  extraSeatsContracted?: number;
  extraSeatPriceCents: number;
  billingCycle?: 'Monthly' | 'Semiannual' | 'Yearly';
  currentPeriodEnd?: string | null;
  nextBillingDate?: string | null;
  addOns: SubscriptionAddOn[];
  /** ID do usuário dono da conta (criador do workspace). Não pode ser removido de assentos nem desativado em add-on. Back: company.ownerUserId ou equivalente. */
  ownerUserId?: number | null;
  /** Alterações agendadas para o próximo ciclo. GET /api/subscriptions/current. */
  scheduledChanges?: ScheduledChange[];
  /** Se a assinatura será cancelada ao fim do período. */
  cancelAtPeriodEnd?: boolean;
}

export interface SubscriptionAddOn {
  code: string;
  pricingModel: 'PerSubscription' | 'PerUser';
  priceCents: number;
  enabledUsersCount: number;
  /** IDs dos usuários com o add-on habilitado (opcional; quando o back enviar, o modal marca "Habilitado"). */
  enabledUserIds?: number[];
}

export interface StartTrialRequest {
  planCode: PlanCode;
}

export interface StartTrialResponse {
  subscriptionId: number;
  status: string;
  trialEndsAt: string;
  seatsIncluded: number;
  nextStep: string;
}

export interface UpgradePlanRequest {
  planCode: PlanCode;
  billingCycle: 'Monthly' | 'Semiannual' | 'Yearly';
  paymentMethod: PaymentMethod;
}

export interface SubscriptionSeat {
  id: number;
  userId: number;
  userName: string;
  userEmail: string;
  seatStatus: 'Active' | 'Suspended' | 'Removed';
  isBillable: boolean;
  activatedAt: string;
  deactivatedAt: string | null;
  /** Se true, é o proprietário (criador da conta); não pode ser removido. Backend deve enviar. */
  isOwner?: boolean;
}

export interface SubscriptionSeatsResponse {
  data: SubscriptionSeat[];
}

export interface AddSeatRequest {
  /** Opcional quando o fluxo é "comprar assento vazio"; depois o cliente cadastra quem ocupa na tela Usuários. */
  userId?: number;
  paymentMethod: PaymentMethod;
  /** Quantidade de assentos a adicionar (default 1). Backend pode retornar checkout com valor total. */
  quantity?: number;
}

export interface AddOn {
  id: number;
  code: string;
  name: string;
  pricingModel: 'PerUser';
  priceCents: number;
}

export interface AddOnsResponse {
  data: AddOn[];
}

/** Item do histórico de pagamentos (GET /api/subscriptions/payment-history) */
export interface PaymentHistoryItem {
  id: number;
  invoiceNumber: string;
  period: string;
  amountCents: number;
  status: 'Pending' | 'Paid' | 'Canceled';
  paymentMethod: 'Pix' | 'CreditCard' | 'BankSlip' | null;
  dueDate: string;
  paidAt: string | null;
  planName: string;
  pdfUrl: string | null;
  createdAt: string;
}

export interface PaymentHistoryResponse {
  data: PaymentHistoryItem[];
}

export interface ContractAddOnRequest {
  addOnCode: string;
  /** Cobrança na hora: método de pagamento (obrigatório ao contratar). */
  paymentMethod?: PaymentMethod;
}

/** Métodos aceitos pelo checkout. BankTransfer descontinuado — não exibir nem enviar. */
export type PaymentMethod = 'Pix' | 'CreditCard' | 'BankSlip';

export interface CheckoutRequest {
  paymentMethod: PaymentMethod;
}

export interface CheckoutResponse {
  invoiceId: number;
  paymentMethod: PaymentMethod;
  /** Valor real do PIX (ex.: 10 = R$ 0,10 em modo teste). QR Code usa este valor. */
  amountCents: number;
  /** Quando preenchido (ex.: 14900): valor do plano para exibir no resumo. Título do plano usa este valor; valor a pagar continua sendo amountCents. */
  displayAmountCents?: number;
  qrCode?: string; // Para PIX (string)
  qrCodeBase64?: string; // Para PIX (imagem base64)
  paymentUrl?: string; // Para Stripe (Cartão/Boleto/Transferência)
  expiresAt: string;
  gatewayTransactionId: string;
}

export interface CheckoutPreview {
  planCode: string;
  planName: string;
  billingCycle: 'Monthly' | 'Semiannual' | 'Yearly';
  planPriceCents: number;
  extraSeatsCount: number;
  extraSeatsPriceCents: number;
  extraSeatsAmountCents: number;
  addOns: Array<{
    code: string;
    name: string;
    amountCents: number;
    quantity: number;
  }>;
  addOnsTotalCents: number;
  totalAmountCents: number;
  discountPercent: number;
  discountDescription?: string | null;
}

/** Body para POST /api/subscriptions/scheduled-changes (alteração no próximo ciclo). */
export type ScheduleChangeRequest =
  | { changeType: 'PlanDowngrade'; newPlanCode: string }
  | { changeType: 'SeatReduction'; targetExtraSeats: number }
  | { changeType: 'AddOnRemoval'; addOnCode: string }
  | { changeType: 'CycleToMonthly' };

/** Resposta 200 de POST /api/subscriptions/cancel. */
export interface CancelSubscriptionResponse {
  eligivelReembolso: boolean;
  dataLimiteReembolso?: string | null;
  regrasMensagem?: string | null;
  oQueSeraFeito?: string | null;
  cancelamentoEfetivoEm?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  /** Spec: GET {baseUrl}/api/Plans (P maiúsculo) — público */
  private readonly plansEndpoint = 'Plans';
  private readonly subscriptionsEndpoint = 'subscriptions';
  private readonly seatsEndpoint = 'subscriptions/seats';
  private readonly addOnsEndpoint = 'addons';
  private readonly subscriptionAddOnsEndpoint = 'subscriptions/addons';

  constructor(private apiService: ApiService) {}

  /**
   * Lista todos os planos disponíveis (GET /api/Plans — público). Em erro, log no Console para identificar (ver docs/PRECOS_PLANOS_API.md).
   */
  getPlans(): Observable<PlanListResponse> {
    return this.apiService.get<PlanListResponse>(this.plansEndpoint).pipe(
      catchError((err) => {
        logPlansError('GET Plans', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Histórico de pagamentos (GET /api/subscriptions/payment-history).
   * 204 ou erro → tratar como data: [].
   */
  getPaymentHistory(): Observable<PaymentHistoryItem[]> {
    return this.apiService.get<PaymentHistoryResponse>(`${this.subscriptionsEndpoint}/payment-history`).pipe(
      map((res) => res?.data ?? []),
      catchError((err) => {
        if (err?.status === 204) return of([]);
        return of([]);
      })
    );
  }

  /**
   * Obtém a assinatura atual da empresa
   * Retorna null se status for 204 (NoContent)
   * O HttpClient do Angular normalmente não lança erro para 204, retorna body vazio
   */
  getCurrentSubscription(): Observable<Subscription | null> {
    return this.apiService.get<Subscription>(`${this.subscriptionsEndpoint}/current`).pipe(
      map((response) => {
        // Se a resposta for vazia ou null, significa 204 NoContent
        if (!response || (typeof response === 'object' && Object.keys(response).length === 0)) {
          return null;
        }
        // Normalizar addOns: back pode enviar enabledUserIds (camelCase) ou EnabledUserIds (PascalCase)
        if (response.addOns && Array.isArray(response.addOns)) {
          response.addOns = response.addOns.map((addOn) => {
            const raw = addOn as { enabledUserIds?: number[]; EnabledUserIds?: number[] };
            const ids = raw.enabledUserIds ?? raw.EnabledUserIds;
            return { ...addOn, enabledUserIds: Array.isArray(ids) ? ids : [] };
          });
        }
        // Normalizar seatsTotal (spec back: GET current devolve seatsTotal)
        const raw = response as {
          seatsTotal?: number; SeatsTotal?: number;
          ownerUserId?: number; OwnerUserId?: number; ownerId?: number; OwnerId?: number;
          company?: { ownerUserId?: number; OwnerUserId?: number; ownerId?: number; OwnerId?: number };
          scheduledChanges?: ScheduledChange[]; ScheduledChanges?: ScheduledChange[];
          cancelAtPeriodEnd?: boolean; CancelAtPeriodEnd?: boolean;
        };
        if (raw.seatsTotal != null || raw.SeatsTotal != null) {
          (response as any).seatsTotal = raw.seatsTotal ?? raw.SeatsTotal;
        }
        const ownerId = raw.ownerUserId ?? raw.OwnerUserId ?? raw.ownerId ?? raw.OwnerId
          ?? raw.company?.ownerUserId ?? raw.company?.OwnerUserId ?? raw.company?.ownerId ?? raw.company?.OwnerId;
        if (ownerId != null) {
          (response as any).ownerUserId = Number(ownerId);
        }
        const scheduledChanges = raw.scheduledChanges ?? raw.ScheduledChanges;
        if (Array.isArray(scheduledChanges)) {
          (response as any).scheduledChanges = scheduledChanges;
        }
        if (raw.cancelAtPeriodEnd != null || raw.CancelAtPeriodEnd != null) {
          (response as any).cancelAtPeriodEnd = raw.cancelAtPeriodEnd ?? raw.CancelAtPeriodEnd;
        }
        return response;
      }),
      catchError((error) => {
        // Se for 204, o HttpClient pode não lançar erro, mas se lançar, tratar aqui
        if (error.status === 204) {
          return of(null);
        }
        // Para outros erros (500, 401, etc), propagar o erro
        throw error;
      })
    );
  }

  /**
   * Inicia um trial para um plano
   */
  startTrial(request: StartTrialRequest): Observable<StartTrialResponse> {
    return this.apiService.post<StartTrialResponse>(
      `${this.subscriptionsEndpoint}/trial`,
      request
    );
  }

  /**
   * Faz upgrade do plano atual. Retorna checkout (PIX/Stripe); a mudança de plano só é aplicada após confirmação do pagamento (webhook).
   * Ver docs/FRONT_SPEC_COBRANCA_NA_HORA.md.
   */
  upgradePlan(request: UpgradePlanRequest): Observable<CheckoutResponse> {
    return this.apiService.post<CheckoutResponse>(
      `${this.subscriptionsEndpoint}/upgrade`,
      request
    );
  }

  /**
   * Cancela a assinatura atual. Resposta 200 com mensagens para exibir (regras, reembolso, data efetiva).
   */
  cancelSubscription(): Observable<CancelSubscriptionResponse> {
    return this.apiService.post<CancelSubscriptionResponse>(
      `${this.subscriptionsEndpoint}/cancel`,
      {}
    );
  }

  /**
   * Agenda alteração para o próximo ciclo (downgrade, redução de assentos, remoção de add-on, ciclo → mensal).
   * POST /api/subscriptions/scheduled-changes. Resposta 204 em sucesso.
   */
  scheduleChange(body: ScheduleChangeRequest): Observable<void> {
    return this.apiService.post<void>(
      `${this.subscriptionsEndpoint}/scheduled-changes`,
      body
    );
  }

  /**
   * Lista todos os assentos da assinatura atual
   */
  getSeats(): Observable<SubscriptionSeatsResponse> {
    return this.apiService.get<SubscriptionSeatsResponse>(this.seatsEndpoint);
  }

  /**
   * Adiciona assento(s) à assinatura. Retorna checkout (PIX/Stripe); assento(s) criado(s) após confirmação do pagamento (webhook).
   * Spec back: POST /api/subscriptions/seats com body camelCase:
   *   { "paymentMethod": "Pix"|"CreditCard"|"BankSlip", "quantity": 1 } (sem userId = comprar para atribuir depois)
   *   ou { "userId": 42, "paymentMethod": "Pix" } (1 assento para o usuário).
   * Resposta esperada: 201 + checkout. Se o back retornar 400, a mensagem em errors[] é exibida no front.
   */
  addSeat(request: AddSeatRequest): Observable<CheckoutResponse> {
    const body: Record<string, unknown> = {
      paymentMethod: String(request.paymentMethod),
    };
    if (request.userId != null) {
      body['userId'] = Number(request.userId);
    } else {
      body['quantity'] = request.quantity != null && request.quantity > 0 ? Number(request.quantity) : 1;
    }
    return this.apiService.post<CheckoutResponse>(this.seatsEndpoint, body);
  }

  /**
   * Remove um assento da assinatura
   */
  removeSeat(seatId: number): Observable<void> {
    return this.apiService.delete<void>(`${this.seatsEndpoint}/${seatId}`);
  }

  /**
   * Lista todos os add-ons disponíveis
   */
  getAddOns(): Observable<AddOnsResponse> {
    return this.apiService.get<AddOnsResponse>(this.addOnsEndpoint);
  }

  /**
   * Contrata um add-on para a assinatura atual (cobrança na hora).
   * 201 = checkout (abrir modal de pagamento); 204 = add-on grátis (só atualizar tela).
   */
  contractAddOn(request: ContractAddOnRequest): Observable<CheckoutResponse> {
    return this.apiService.post<CheckoutResponse>(this.subscriptionAddOnsEndpoint, request);
  }

  /**
   * Habilita um usuário para usar um add-on
   */
  enableAddOnForUser(addOnCode: string, userId: number): Observable<void> {
    return this.apiService.post<void>(
      `${this.subscriptionAddOnsEndpoint}/${addOnCode}/users/${userId}/enable`,
      {}
    );
  }

  /**
   * Cria checkout para habilitar add-on para vários usuários (cobrança na hora).
   * POST /api/subscriptions/addons/{code}/users/checkout com { userIds, paymentMethod }.
   * Retorna checkout (PIX/Cartão); após pagamento o back pode habilitar via webhook ou o front chama enable para cada usuário.
   */
  enableAddOnUsersCheckout(addOnCode: string, userIds: number[], paymentMethod: PaymentMethod): Observable<CheckoutResponse> {
    const body = { userIds, paymentMethod };
    return this.apiService.post<CheckoutResponse>(
      `${this.subscriptionAddOnsEndpoint}/${addOnCode}/users/checkout`,
      body
    );
  }

  /**
   * Desabilita um usuário de usar um add-on
   */
  disableAddOnForUser(addOnCode: string, userId: number): Observable<void> {
    return this.apiService.post<void>(
      `${this.subscriptionAddOnsEndpoint}/${addOnCode}/users/${userId}/disable`,
      {}
    );
  }

  /**
   * Lista IDs dos usuários que têm o add-on habilitado.
   * GET /api/subscriptions/addons/{code}/users → { userIds: number[] } ou { data: { userIds: number[] } }
   */
  getAddOnEnabledUsers(addOnCode: string): Observable<number[]> {
    return this.apiService.get<{ userIds?: number[]; data?: number[] | { userIds?: number[] } }>(
      `${this.subscriptionAddOnsEndpoint}/${addOnCode}/users`
    ).pipe(
      map((res) => {
        if (Array.isArray(res?.userIds)) return res.userIds;
        if (Array.isArray(res?.data)) return res.data;
        if (res?.data && typeof res.data === 'object' && Array.isArray((res.data as any).userIds)) {
          return (res.data as any).userIds;
        }
        return [];
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Obtém preview/simulação do valor do checkout (GET .../checkout/preview — Bearer obrigatório). Em erro, log no Console (ver docs/PRECOS_PLANOS_API.md).
   */
  getCheckoutPreview(billingCycle?: 'Monthly' | 'Semiannual' | 'Yearly'): Observable<CheckoutPreview> {
    let url = `${this.subscriptionsEndpoint}/checkout/preview`;
    if (billingCycle) {
      url += `?billingCycle=${billingCycle}`;
    }
    return this.apiService.get<CheckoutPreview>(url).pipe(
      catchError((err) => {
        logPlansError('Checkout preview', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Cria um checkout/pagamento para a assinatura
   */
  createCheckout(request: CheckoutRequest): Observable<CheckoutResponse> {
    return this.apiService.post<CheckoutResponse>(
      `${this.subscriptionsEndpoint}/checkout`,
      request
    );
  }

  /**
   * SPEC §6 (opção recomendada): Simular confirmação PIX — POST /api/subscriptions/simulate-pix com Bearer.
   * 204 = sucesso; 400 = gatewayTransactionId inválido; 403 = simulação não permitida no ambiente.
   */
  simulatePix(gatewayTransactionId: string): Observable<void> {
    return this.apiService.postVoid(
      `${this.subscriptionsEndpoint}/simulate-pix`,
      { gatewayTransactionId }
    );
  }
}
